import { useState } from 'react';
import { motion } from 'framer-motion';
import { Key, Zap, Menu, X } from 'lucide-react';
import { useCypherpunk } from '../context/CypherpunkContext';
import { useHeaderVisibility } from '../hooks/useHeaderVisibility';

const navLinks = [
  { label: 'WHO IS ZATO?', href: '#about' },
  { label: 'ARCHIVE', href: '#nakamoto' },
  { label: 'VAULT', href: '#vault' },
  { label: 'PRINCIPLES', href: '#principles' },
  { label: 'TRANSMISSIONS', href: '#transmissions' },
  { label: 'DROPS', href: '#drops' },
  { label: 'SECURE CHANNEL', href: '#contact' },
];

function slugify(label) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function CypherToggle({ cypherpunkMode, toggle, iconOnly }) {
  return (
    <button
      onClick={toggle}
      className={iconOnly ? `cypher-toggle-icon ${cypherpunkMode ? 'cypher-toggle--active' : ''}` : `cypher-toggle ${cypherpunkMode ? 'cypher-toggle--active' : ''}`}
      data-testid="cypherpunk-toggle"
      title={cypherpunkMode ? 'Disable Cypherpunk Mode' : 'Enable Cypherpunk Mode'}
      aria-label="Toggle cypherpunk mode"
    >
      {cypherpunkMode ? <Zap size={iconOnly ? 18 : 14} /> : <Key size={iconOnly ? 18 : 14} />}
      {!iconOnly && <span className="cypher-toggle-text">{cypherpunkMode ? 'ENCRYPTED' : 'DECRYPT'}</span>}
    </button>
  );
}

function MobileHeader({ cypherpunkMode, toggle, menuOpen, setMenuOpen, scrollTo }) {
  return (
    <div className="md:hidden grid grid-cols-3 items-center px-5 py-4">
      <CypherToggle cypherpunkMode={cypherpunkMode} toggle={toggle} iconOnly />

      <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="flex justify-center" data-testid="nav-logo">
        <img src="/zato-mask.png" alt="Zato" className="h-8 w-auto opacity-90" />
      </a>

      <button
        className="flex justify-end text-white/70 hover:text-white transition-colors"
        onClick={() => setMenuOpen(!menuOpen)}
        data-testid="mobile-menu-toggle"
        aria-label="Toggle menu"
      >
        {menuOpen ? <X size={18} /> : <Menu size={18} />}
      </button>
    </div>
  );
}

function DesktopHeader({ cypherpunkMode, toggle, scrollTo }) {
  return (
    <div className="hidden md:flex max-w-7xl mx-auto px-6 py-4 items-center justify-between">
      <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="nav-logo flex items-center gap-2" data-testid="nav-logo">
        <img src="/zato-mask.png" alt="Zato mask" className="h-9 w-auto opacity-100" />
      </a>

      <div className="flex items-center gap-6">
        {navLinks.map((link) => (
          <a
            key={link.href}
            href={link.href}
            onClick={(e) => scrollTo(e, link.href)}
            className="nav-link"
            data-testid={`nav-link-${slugify(link.label)}`}
          >
            {link.label}
          </a>
        ))}
      </div>

      <CypherToggle cypherpunkMode={cypherpunkMode} toggle={toggle} />
    </div>
  );
}

function MobileMenu({ menuOpen, scrollTo }) {
  if (!menuOpen) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="md:hidden bg-black/95 border-t border-white/5 px-6 py-4 flex flex-col gap-4"
    >
      {navLinks.map((link) => (
        <a
          key={link.href}
          href={link.href}
          onClick={(e) => scrollTo(e, link.href)}
          className="nav-link text-sm"
          data-testid={`mobile-nav-link-${slugify(link.label)}`}
        >
          {link.label}
        </a>
      ))}
    </motion.div>
  );
}

export function Navigation() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { cypherpunkMode, toggle } = useCypherpunk();
  const { scrolled, hidden } = useHeaderVisibility(menuOpen);

  const scrollTo = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-[#050505] transition-all duration-300 ease-in-out ${
        scrolled ? 'border-b border-white/5' : ''
      } ${hidden ? '-translate-y-full opacity-0 pointer-events-none md:translate-y-0 md:opacity-100 md:pointer-events-auto' : 'translate-y-0 opacity-100'}`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="main-nav"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <MobileHeader cypherpunkMode={cypherpunkMode} toggle={toggle} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrollTo={scrollTo} />
        <DesktopHeader cypherpunkMode={cypherpunkMode} toggle={toggle} scrollTo={scrollTo} />
        <MobileMenu menuOpen={menuOpen} scrollTo={scrollTo} />
      </motion.div>
    </nav>
  );
}
